/*
  warranty-product-content.js
  ------------------------------------------------------------------
  Presentation-only enhancement for the generated HondaCare description
  on product.warranty-product pages.

  HARD RULES (do not relax):
    - Never adds, removes or rewords customer-facing text. Every
      transform re-wraps the exact same characters: label + separator +
      remainder always concatenates back to the original string.
    - Never hides meaningful content. The only thing visually suppressed
      is the ":" separator, and it stays in the DOM via .visually-hidden
      so assistive tech and crawlers still receive it.
    - Never required for the page to work. All text is server-rendered;
      this only adds classes and decorative <use> icons on top. If it
      throws or never runs, the CSS fallback is a clean editorial page.

  WHY IT MATCHES ON TEXT PATTERNS AND NOT ON HEADINGS
    The 44 live warranty products use ~230 distinct section headings and
    ~50 distinct feature-card headings - the generator writes them
    freely. Matching heading text would style Dax 125 correctly and
    misfire on most other products.

    What IS consistent is the shape of the copy itself:
      "Nationwide coverage: Coverage is accepted at ..."   (label form)
      "Engine system examples include transmission, ..."   (system form)
    Those patterns are matched instead, so a section earns the richer
    treatment only when its own content supports it, and any section
    that does not match simply stays clean editorial text.
  ------------------------------------------------------------------ */

(function () {
  'use strict';

  var NS = 'warranty-description';

  /* A leading "Label: " - short, no sentence punctuation inside. */
  var LABEL_RE = /^([^.:;<>()]{3,46}):(\s*)/;

  /* "<system> system examples include " / "... examples include " */
  var SYSTEM_RE = /^(.{2,60}?\bexamples\s+include)(\s+)/i;

  /* Some generated descriptions restate the price matrix as prose
     ("1 Year: $320.00 Without Roadside or ..."). Those paragraphs match
     the label shape but are pricing, not benefits - badging them with a
     tick would read as a claim. Any paragraph containing a currency
     amount is left as plain editorial text. */
  var PRICE_RE = /\$\s*\d/;

  /* A labelled paragraph whose label is really a covered-system heading
     ("Fuel system examples: carburetors, ...") belongs in the systems
     treatment, not the benefits one. */
  var SYSTEM_LABEL_RE = /\bexamples?\b|\bsystems?\b/i;

  /* Keyword -> sprite id. First match wins, so order matters. */
  var SYSTEM_ICONS = [
    ['engine', 'engine'],
    ['drive', 'gear'],
    ['transmission', 'gear'],
    ['cool', 'fan'],
    ['radiator', 'fan'],
    ['fuel', 'fuel'],
    ['electric', 'bolt'],
    ['brake', 'brake'],
    ['chassis', 'chassis'],
    ['suspension', 'chassis']
  ];

  var BENEFIT_ICONS = [
    /* Neutral-first: a tick beside "Void conditions" or "Exclusions"
       would read as an endorsement, so those take the notes icon.
       None of these strings occur in the five canonical benefit labels. */
    ['exclusion', 'doc'],
    ['excluded', 'doc'],
    ['void', 'doc'],
    ['eligib', 'doc'],
    ['brochure', 'doc'],
    ['disclaim', 'doc'],
    ['nationwide', 'pin'],
    ['dealer', 'pin'],
    ['accepted', 'pin'],
    ['mileage', 'gauge'],
    ['hour limit', 'gauge'],
    ['deductible', 'tag'],
    ['co-pay', 'tag'],
    ['copay', 'tag'],
    ['transferab', 'users'],
    ['owner', 'users'],
    ['repair', 'wrench'],
    ['genuine', 'wrench'],
    ['technician', 'wrench'],
    ['roadside', 'road'],
    ['tow', 'road'],
    ['warranty', 'shield'],
    ['coverage', 'shield']
  ];

  var FEATURE_ICONS = [
    ['term', 'calendar'],
    ['year', 'calendar'],
    ['month', 'calendar'],
    ['choose', 'calendar'],
    ['pick', 'calendar'],
    ['roadside', 'road'],
    ['dealer', 'pin'],
    ['nationwide', 'pin'],
    ['accepted', 'pin'],
    ['benefit', 'star'],
    ['deductible', 'tag'],
    ['transferab', 'users'],
    ['repair', 'wrench']
  ];

  function pickIcon(table, text, fallback) {
    var t = (text || '').toLowerCase();
    for (var i = 0; i < table.length; i++) {
      if (t.indexOf(table[i][0]) !== -1) return table[i][1];
    }
    return fallback;
  }

  function iconEl(name, className) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', className);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', '#wicon-' + name);
    svg.appendChild(use);
    return svg;
  }

  function span(cls, text) {
    var el = document.createElement('span');
    el.className = cls;
    el.textContent = text;
    return el;
  }

  /* Only touch paragraphs that are pure text. If the generator ever
     nests a link or <strong> inside, leave that paragraph alone. */
  function isPlainText(p) {
    return p && p.children.length === 0 && (p.textContent || '').trim().length > 0;
  }

  function classify(p) {
    if (!isPlainText(p)) return null;
    var text = p.textContent.trim();
    if (PRICE_RE.test(text)) return null;

    var sys = SYSTEM_RE.exec(text);
    if (sys) {
      return { kind: 'system', label: sys[1], sep: sys[2], rest: text.slice(sys[0].length) };
    }
    var lab = LABEL_RE.exec(text);
    if (lab && lab[1].trim().split(/\s+/).length <= 8) {
      return {
        kind: SYSTEM_LABEL_RE.test(lab[1]) ? 'system' : 'benefit',
        label: lab[1],
        sep: ':' + lab[2],
        rest: text.slice(lab[0].length)
      };
    }
    return null;
  }

  /* label + sep + rest === original text. Nothing is added or dropped. */
  function rebuild(p, parts, iconName, blockCls) {
    if (!parts.rest) return false;
    var frag = document.createDocumentFragment();
    frag.appendChild(iconEl(iconName, NS + '__item-icon'));
    var body = document.createElement('span');
    body.className = NS + '__item-body';
    body.appendChild(span(NS + '__item-label', parts.label));
    body.appendChild(span('visually-hidden', parts.sep));
    body.appendChild(span(NS + '__item-copy', parts.rest));
    frag.appendChild(body);
    p.textContent = '';
    p.appendChild(frag);
    p.classList.add(blockCls);
    return true;
  }

  function enhanceFeatureCards(root) {
    var cards = root.querySelectorAll('.' + NS + '__feature-card');
    for (var i = 0; i < cards.length; i++) {
      var title = cards[i].querySelector('.' + NS + '__feature-title');
      if (!title || title.querySelector('svg')) continue;
      var name = pickIcon(FEATURE_ICONS, title.textContent, 'shield');
      title.insertBefore(iconEl(name, NS + '__feature-icon'), title.firstChild);
      cards[i].classList.add(NS + '__feature-card--iconed');
    }
    var strip = root.querySelector('.' + NS + '__features');
    if (strip) strip.classList.add(NS + '__features--enhanced');
  }

  function enhanceSections(root) {
    var sections = root.querySelectorAll('section.' + NS + '__section');

    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      var paras = section.querySelectorAll('.' + NS + '__section-copy');
      var found = [];
      var systems = 0;
      var benefits = 0;

      for (var j = 0; j < paras.length; j++) {
        var parts = classify(paras[j]);
        found.push(parts);
        if (!parts) continue;
        if (parts.kind === 'system') systems++;
        else benefits++;
      }

      /* Two or more matching paragraphs is the threshold for promoting a
         section to a richer layout. A single stray match stays inline so
         ordinary prose is never turned into a one-item grid. */
      var mode = null;
      if (systems >= 2) mode = 'system';
      else if (benefits >= 2) mode = 'benefit';
      if (!mode) continue;

      var applied = 0;
      for (var k = 0; k < paras.length; k++) {
        var p = found[k];
        if (!p || p.kind !== mode) continue;
        var icon =
          mode === 'system'
            ? pickIcon(SYSTEM_ICONS, p.label, 'gear')
            : pickIcon(BENEFIT_ICONS, p.label, 'check');
        if (rebuild(paras[k], p, icon, NS + (mode === 'system' ? '__system' : '__benefit'))) applied++;
      }

      if (applied >= 2) {
        section.classList.add(NS + '__section--' + (mode === 'system' ? 'systems' : 'benefits'));
        /* Paragraphs that did not match keep their normal flow and are
           marked so the grid can span them full width. */
        for (var m = 0; m < paras.length; m++) {
          if (!paras[m].classList.contains(NS + '__benefit') && !paras[m].classList.contains(NS + '__system')) {
            paras[m].classList.add(NS + '__section-copy--lead');
          }
        }
      }
    }
  }

  function enhanceBrochure(root) {
    var title = root.querySelector('.' + NS + '__brochure-title');
    if (!title || title.querySelector('svg')) return;
    title.insertBefore(iconEl('doc', NS + '__brochure-icon'), title.firstChild);
  }

  function init() {
    var root = document.querySelector('.' + NS);
    if (!root || root.dataset.warrantyEnhanced === 'true') return;
    root.dataset.warrantyEnhanced = 'true';
    try {
      enhanceFeatureCards(root);
      enhanceSections(root);
      enhanceBrochure(root);
      root.classList.add(NS + '--enhanced');
    } catch (e) {
      /* Presentation only - never break the page over styling. */
      if (window.console && console.warn) console.warn('warranty content enhancement skipped:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
